class StencilPreview {
  constructor() {
    this.defaultFont = 'Marsh Stencil';
    this.defaultColor = 'rgba(0,0,0,0.9)';
    this.previousCartState = null;
    this.init();
    this.attachCartUpdateListener();
  }

  init() {
    this.inputs = document.querySelectorAll('[data-stencil-input]');
    this.charCounters = document.querySelectorAll('[data-char-counter]');
    this.toggles = document.querySelectorAll('[data-stencil-toggle]');
    this.containers = document.querySelectorAll('[data-stencil-container]');
    this.previewOverlays = document.querySelectorAll('.stencil-preview-overlay');
    this.previews = document.querySelectorAll('[data-stencil-preview]');
    this.fontSelects = document.querySelectorAll('[data-stencil-font]');
    this.colorSelects = document.querySelectorAll('[data-stencil-color]');
    
    this.fontSelects.forEach(select => {
      select.value = this.defaultFont;
      select.addEventListener('change', this.handleFontChange.bind(this));
    });

    this.colorSelects.forEach(select => {
      select.value = this.defaultColor;
      select.addEventListener('change', this.handleColorChange.bind(this));
    });

    this.updatePreviewFonts(this.defaultFont);
    this.updatePreviewColors(this.defaultColor);
    
    this.inputs.forEach(input => {
      input.addEventListener('input', this.handleInput.bind(this));
      if (input.value) {
        this.updateCharCounter(input);
      }
    });

    this.toggles.forEach(toggle => {
      toggle.addEventListener('change', this.handleToggle.bind(this));
      if (toggle.checked) {
        this.showStencilInput();
      } else {
        this.hideStencilInput();
      }
    });

    if (!Array.from(this.toggles).some(toggle => toggle.checked)) {
      this.hidePreviewOverlays();
    }
  }

  attachCartUpdateListener() {

    this.updateCartState();
    

    this.skipNextUpdate = false;
    

    if (window.publish && window.PUB_SUB_EVENTS) {
      document.addEventListener('pubsub:publish', (event) => {
        if (event.detail?.eventType === 'cartUpdate' && 
            (event.detail?.data?.source === 'bundle-manager-stencil' || 
             event.detail?.data?.source === 'bundle-manager-addon')) {
          this.skipNextUpdate = true;
          this.updateCartState();
        }
      });
    }
    
    // Listen for cart update events (fired after successful add-to-cart)
    document.documentElement.addEventListener('cart:refresh', () => {
      if (this.skipNextUpdate) {
        this.skipNextUpdate = false;
        return;
      }
      this.handleCartUpdate();
    });
    
    document.addEventListener('rebuy:cart.change', () => {
      if (this.skipNextUpdate) {
        this.skipNextUpdate = false;
        return;
      }
      this.handleCartUpdate();
    });
  }

  async updateCartState() {
    try {
      const cartResponse = await fetch('/cart.js');
      this.previousCartState = await cartResponse.json();
    } catch (error) {
      console.error('Failed to fetch cart state:', error);
    }
  }

  async handleCartUpdate() {
    const stencilData = this.getStencilData();
    if (!stencilData) {
      await this.updateCartState();
      return;
    }

    try {
      const cartResponse = await fetch('/cart.js');
      const currentCart = await cartResponse.json();
      const newLineItem = this.findNewlyAddedLineItem(this.previousCartState, currentCart);
      
      if (!newLineItem) {
        await this.updateCartState();
        return;
      }

      const hasStencilData = newLineItem.properties && newLineItem.properties.stitch;
      if (hasStencilData) {
        await this.updateCartState();
        return;
      }

      await this.updateLineItemWithStencil(newLineItem.key, stencilData);
      await this.updateCartState();
      
    } catch (error) {
      console.error('Failed to add stencil attributes:', error);
      await this.updateCartState();
    }
  }

  findNewlyAddedLineItem(previousCart, currentCart) {
    if (!previousCart || !currentCart) return null;

    for (const currentItem of currentCart.items) {
      const existedBefore = previousCart.items.find(prevItem => 
        prevItem.key === currentItem.key
      );
      
      if (!existedBefore) {
        return currentItem;
      }
      
      const previousItem = previousCart.items.find(prevItem => 
        prevItem.variant_id === currentItem.variant_id &&
        JSON.stringify(prevItem.properties) === JSON.stringify(currentItem.properties)
      );
      
      if (previousItem && currentItem.quantity > previousItem.quantity) {
        return null;
      }
    }
    
    return null;
  }

  async updateLineItemWithStencil(lineItemKey, stencilData) {
    let currentQuantity = 1;
    try {
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      const currentItem = cart.items.find(item => item.key === lineItemKey);
      if (currentItem) {
        currentQuantity = currentItem.quantity;
      }
    } catch (error) {
      console.error('Failed to get current cart state:', error);
    }

    const body = JSON.stringify({
      id: lineItemKey,
      quantity: currentQuantity,
      properties: {
        stitch: stencilData.stitch,
        text: stencilData.text,
        font: stencilData.font,
        color: stencilData.color
      }
    });

    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: body
    });

    if (!response.ok) {
      throw new Error(`Failed to update line item: ${response.statusText}`);
    }


    this.clearStencilForm();
    
    return await response.json();
  }

  getStencilData() {
    const stencilToggle = document.querySelector('[data-stencil-toggle]');
    if (!stencilToggle || !stencilToggle.checked) return null;

    const stencilTextInput = document.querySelector('[data-stencil-input]');
    const stencilFontSelect = document.querySelector('[data-stencil-font]');
    const stencilColorSelect = document.querySelector('[data-stencil-color]');
    
    const stencilText = stencilTextInput?.value?.trim() || '';
    if (!stencilText) return null;

    const stencilFont = stencilFontSelect?.value || this.defaultFont;
    const stencilColor = stencilColorSelect?.selectedOptions[0]?.textContent || 'Black';

    return {
      stitch: 'true',
      text: stencilText,
      font: stencilFont,
      color: stencilColor
    };
  }

  clearStencilForm() {
    const stencilToggle = document.querySelector('[data-stencil-toggle]');
    const stencilTextInput = document.querySelector('[data-stencil-input]');
    
    if (stencilToggle) stencilToggle.checked = false;
    if (stencilTextInput) stencilTextInput.value = '';
    

    this.hideStencilInput();
  }

  handleInput(event) {
    const input = event.target;
    

    if (this.previews) {
      this.previews.forEach(preview => {
        preview.textContent = input.value;
      });
    }


    this.updateCharCounter(input);
  }

  handleFontChange(event) {
    const fontFamily = event.target.value;
    this.updatePreviewFonts(fontFamily);
  }

  handleColorChange(event) {
    const color = event.target.value;
    this.updatePreviewColors(color);
  }

  updatePreviewFonts(fontFamily) {

    if (this.previews) {
      this.previews.forEach(preview => {
        preview.style.setProperty('font-family', fontFamily, 'important');
      });
    }
    

    if (this.previewOverlays) {
      this.previewOverlays.forEach(overlay => {
        const preview = overlay.querySelector('[data-stencil-preview]');
        if (preview) {
          preview.style.setProperty('font-family', fontFamily, 'important');
        }
      });
    }
  }

  updatePreviewColors(color) {

    if (this.previews) {
      this.previews.forEach(preview => {
        preview.style.setProperty('--text-color', color);
      });
    }
    

    if (this.previewOverlays) {
      this.previewOverlays.forEach(overlay => {
        const preview = overlay.querySelector('[data-stencil-preview]');
        if (preview) {
          preview.style.setProperty('--text-color', color);
        }
      });
    }
  }

  handleToggle(event) {
    if (event.target.checked) {
      this.showStencilInput();
      this.switchToStencilImage();
    } else {
      this.hideStencilInput();
    }
  }

  showStencilInput() {
    this.containers.forEach(container => {
      container.style.display = 'block';
    });
    this.previewOverlays.forEach(overlay => {
      overlay.style.display = 'block';
    });

    if (this.inputs[0]) {
      this.inputs[0].focus();
    }

    this.fontSelects.forEach(select => {
      select.value = this.defaultFont;
    });
    this.updatePreviewFonts(this.defaultFont);
  }

  hideStencilInput() {
    this.containers.forEach(container => {
      container.style.display = 'none';
    });
    this.hidePreviewOverlays();

    if (this.inputs[0]) {
      this.inputs[0].value = '';
      this.updateCharCounter(this.inputs[0]);
    }

    if (this.previews) {
      this.previews.forEach(preview => {
        preview.textContent = '';
      });
    }

    this.fontSelects.forEach(select => {
      select.value = this.defaultFont;
    });

    this.colorSelects.forEach(select => {
      select.value = this.defaultColor;
    });
    this.updatePreviewFonts(this.defaultFont);
    this.updatePreviewColors(this.defaultColor);
  }

  hidePreviewOverlays() {
    this.previewOverlays.forEach(overlay => {
      overlay.style.display = 'none';
    });
  }

  switchToStencilImage() {
    if (!window.stencilConfig || !window.stencilConfig.stencilPosition || !window.stencilConfig.sectionId) {
      return;
    }

    const stencilPosition = parseInt(window.stencilConfig.stencilPosition);
    const sectionId = window.stencilConfig.sectionId;
    const mediaGallery = document.querySelector(`media-gallery[id="MediaGallery-${sectionId}"]`);
    
    if (!mediaGallery) return;

    const mediaSlides = mediaGallery.querySelectorAll('[data-media-id]');
    const targetSlide = mediaSlides[stencilPosition - 1];
    
    if (!targetSlide) return;

    const mediaId = targetSlide.getAttribute('data-media-id');
    const slider = mediaGallery.querySelector('[id^="Slider-Gallery-"]');
    
    if (slider) {
      mediaGallery.querySelectorAll('[data-media-id]').forEach(slide => {
        slide.classList.remove('is-active');
      });
      targetSlide.classList.add('is-active');
      
      slider.scrollTo({
        left: targetSlide.offsetLeft,
        behavior: 'smooth'
      });
    }

    const allThumbnails = mediaGallery.querySelectorAll('[data-target]');
    const thumbnail = mediaGallery.querySelector(`[data-target="${mediaId}"]`);
    
    if (thumbnail) {
      const thumbnailButton = thumbnail.querySelector('button');
      if (thumbnailButton) {
        thumbnailButton.click();
        return;
      }
    }
    
    const thumbnailAtPosition = allThumbnails[stencilPosition - 1];
    if (thumbnailAtPosition) {
      const thumbnailButton = thumbnailAtPosition.querySelector('button');
      if (thumbnailButton) {
        thumbnailButton.click();
        
        allThumbnails.forEach(thumb => {
          const button = thumb.querySelector('button');
          if (button) button.removeAttribute('aria-current');
        });
        thumbnailButton.setAttribute('aria-current', 'true');
      }
    }
  }

  updateCharCounter(input) {
    const maxLength = input.maxLength;
    const currentLength = input.value.length;
    
    this.charCounters.forEach(counter => {
      counter.textContent = `${currentLength}/${maxLength}`;
      counter.classList.toggle('is-limit', currentLength === maxLength);
    });
  }
}


if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new StencilPreview();
  });
} else {
  new StencilPreview();
} 